import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { trackClick } from '@/lib/track';
import { trackTrailerPlay } from '@/lib/fanProfile';

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^?&]+)/);
  return match ? match[1] : null;
}

export default function TrailerModal({ film, onClose }) {
  const videoId = extractYouTubeId(film?.links?.trailer || film?.url);

  useEffect(() => {
    if (film && videoId) {
      trackClick({ film: film.title, platform: 'trailer', action: 'modal_open', section: 'experience' });
      trackTrailerPlay(film.title);
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
  }, [film, videoId]);

  const handleClose = useCallback(() => {
    if (film) trackClick({ film: film.title, platform: 'trailer', action: 'modal_close', section: 'experience' });
    onClose();
  }, [film, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  if (!film || !videoId) return null;

  return (
    <div data-testid="trailer-modal" className="fixed inset-0 z-[200] flex items-center justify-center" onClick={handleClose} style={{ animation: 'fadeSlideUp 0.3s var(--ease-out-expo)' }}>
      <div className="absolute inset-0" style={{ background: 'rgba(12, 18, 34, 0.92)' }} />

      <div className="relative z-10 w-[92vw] max-w-5xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <div>
            <p className="font-['Cormorant_Garamond'] italic text-lg md:text-2xl" style={{ color: 'var(--gold)' }}>{film.title}</p>
            <p className="font-['Inter'] text-[10px] tracking-[0.2em] uppercase mt-1" style={{ color: 'var(--ash)' }}>Trailer</p>
          </div>
          <button data-testid="trailer-modal-close" onClick={handleClose} className="transition-colors duration-300 p-2" style={{ color: 'var(--ash)' }}>
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Player — gold glow frame */}
        <div className="relative w-full aspect-video overflow-hidden rounded-sm" style={{ border: '1px solid rgba(201, 168, 76, 0.15)', boxShadow: '0 0 40px rgba(201, 168, 76, 0.06)' }}>
          <iframe
            data-testid="trailer-player"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&color=white&iv_load_policy=3`}
            title={`${film.title} — Trailer`}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            frameBorder="0"
          />
        </div>

        <p className="font-['Inter'] text-[10px] mt-3 text-right" style={{ color: 'rgba(136,136,136,0.3)' }}>Trailer via YouTube</p>
      </div>
    </div>
  );
}
